import Papa from "papaparse";
import moment from "moment";

/** Columns that must exist in the CSV header for upload to proceed */
const REQUIRED_HEADER_KEYS = [
  "make",
  "offerTitle",
  "offerText_rephrased",
  "modelsText",
  "fromDate",
];

function checkRequiredHeaders(metaFields) {
  if (!metaFields || !Array.isArray(metaFields)) return false;
  const set = new Set(metaFields);
  return REQUIRED_HEADER_KEYS.every((key) => set.has(key));
}

export function checkNull(item, keys) {
  const keysToCheck = keys.split(",");
  if (!Array.isArray(keysToCheck)) {
    throw new Error("keysToCheck must be an array");
  }

  return keysToCheck.some((key) => {
    const v = item[key];
    return v === null || v === undefined || v === "";
  });
}

/** Empty row from Papa (trailing newlines) — do not count as invalid */
function isRowEffectivelyEmpty(item) {
  if (!item || typeof item !== "object") return true;
  const vals = Object.values(item);
  return vals.every((v) => v === null || v === undefined || v === "");
}

/**
 * Parse sheet / CSV dates: DD/MM/YYYY or DD-MM-YYYY, or a Date from Excel/dynamicTyping.
 * Returns DD/MM/YYYY string for API upload, or '' if missing/invalid.
 */
function normalizeDateCell(raw) {
  if (raw == null || raw === "") return "";
  if (Object.prototype.toString.call(raw) === "[object Date]") {
    const d = raw;
    if (Number.isNaN(d.getTime())) return "";
    return moment(d).format("DD/MM/YYYY");
  }
  const s = String(raw).trim();
  if (!s) return "";
  const withSlashes = s.replace(/-/g, "/");
  const m = moment(withSlashes, "DD/MM/YYYY", true);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
}

/** Ensures optional columns exist so downstream upload/API never receives undefined values */
function normalizeOfferRow(item) {
  const row = { ...item };
  if (row.state === null || row.state === undefined) row.state = "";
  if (row.offerType === null || row.offerType === undefined)
    row.offerType = "all";
  if (row.image === null || row.image === undefined) row.image = "";
  if (row.toDate === null || row.toDate === undefined) row.toDate = "";
  if (row.car_id === null || row.car_id === undefined) row.car_id = "";
  if (row.toDate && String(row.toDate).trim() !== "") {
    const t = normalizeDateCell(row.toDate);
    row.toDate = t || row.toDate;
  }
  return row;
}

export function validateCSV(file) {
  return new Promise((resolve, reject) => {
    try {
      Papa.parse(file, {
        header: true,
        dynamicTyping: true,
        complete: (results) => {
          const { data, meta } = results;

          if (!checkRequiredHeaders(meta.fields))
            return reject({
              message:
                "Please make sure these columns are present (names are case sensitive): make, offerTitle, offerText_rephrased, modelsText, fromDate. Optional: toDate, state, offerType, image, car_id.",
              error: {},
            });

          let validDataCount = 0,
            invalidDataCount = 0;

          const validData = [];

          data.forEach((item) => {
            if (isRowEffectivelyEmpty(item)) {
              return;
            }

            const fromDateNorm = normalizeDateCell(item.fromDate);
            if (
              checkNull(
                item,
                "make,offerTitle,offerText_rephrased,modelsText"
              ) ||
              !fromDateNorm
            ) {
              invalidDataCount += 1;
            } else {
              validDataCount += 1;
              if (item.make && String(item.make).includes("\n")) {
                item.make = String(item.make).replace("\n", "");
              }

              const row = normalizeOfferRow({
                ...item,
                fromDate: fromDateNorm,
              });
              validData.push(row);
            }
          });

          resolve({
            validDataCount,
            invalidDataCount,
            validData,
          });
        },
      });
    } catch (error) {
      reject({
        message: "File could not be parsed",
        error,
      });
    }
  });
}
